import { useState } from 'react';
import { useRouter } from 'next/router';
import {
  Alert, AlertTitle,
} from '@mui/material';
import { CreatorsListUser } from 'lib/types/users';
import { useUserLists } from 'lib/contexts/userListsContext';
import { CreatorListSkeleton } from 'components/CreatorListSkeleton';
import { CreatorRow } from 'components/CreatorRow';
import { Modal } from 'components/Modal';
import { Application } from 'components/Application';
import { WORK_FOR_US_NAME } from 'lib/constants/teams';
import { TeamFeatureButton } from 'components/TeamFeatureButton';
import { useCurrentUser, useApi } from 'lib/contexts/ApplicationState';
import { pluralize } from 'lib/helpers';
import { useQuery } from '@tanstack/react-query';
import { CreatorStage } from 'lib/types/teams';
import { ListDetailsWrapper } from './ListDetailsWrapper';

interface Props {
  creators: CreatorsListUser[];
  isLoading: boolean;
  error: Error | null;
  publicListId?: string;
  count?: number;
  onListsUpdated?: () => void;
  filteredStages: number[];
}

export const FilterCreatorResults = ({
  creators,
  isLoading,
  error,
  publicListId,
  count,
  onListsUpdated,
  filteredStages,
}: Props) => {
  const { lists } = useUserLists();
  const currentUser = useCurrentUser();
  const router = useRouter();
  const API = useApi();

  const [applicationId, setApplicationId] = useState<undefined | number>();
  const list = lists?.find((l) => l.publicId === publicListId);

  // Query for fetching creator stages
  const {
    data: creatorStages,
  } = useQuery<CreatorStage[]>({
    queryKey: ['getCreatorStages', list?.teamId],
    queryFn: () => API.getCreatorStages(list?.teamId!),
    enabled: !!list?.teamId,
    staleTime: 0, // Always refetch on open
  });

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        Something went wrong fetching data. Please double check your filters!
      </Alert>
    );
  }

  const renderNoCreatorsFound = () => (
    <div className="text-xl font-semibold text-center">
      No creators found for the selected filters
    </div>
  );

  const shouldBlur = !currentUser && !list;

  return (
    <>
      {(creators || []).map((creator, index) => (
        <div className="border-b" key={creator.id}>
          <ListDetailsWrapper
            list={list}
            setApplicationId={setApplicationId}
            user={creator}
            creatorStages={creatorStages}
            filteredStages={filteredStages}
          >
            <CreatorRow
              user={creator}
              context="creator-search"
              blur={shouldBlur && index >= 2}
              onListsUpdated={onListsUpdated}
            />
          </ListDetailsWrapper>
        </div>
      ))}
      <>
        {isLoading && (<div className="mt-4"><CreatorListSkeleton /></div>)}
      </>
      <>
        {!isLoading && !creators?.length && renderNoCreatorsFound()}
      </>
      <>
        {!isLoading && !publicListId && !!creators?.length && !currentUser?.hasPaidTeam && (
          <div className="text-xl font-semibold text-center">
            <div className="p-6 text-center">
              <div className="font-semibold text-xl">
                <span className="mr-2" role="img" aria-label="lock">🔐</span>
                UPGRADE TO GONDOLA FOR BUSINESS TO SEARCH FOR CREATORS
              </div>
              <div className="mt-2">
                <strong>
                  {count?.toLocaleString()}
                  {' '}
                  {pluralize('creator', count)}
                </strong>
                {' '}
                match your filters
                {' '}
                <TeamFeatureButton
                  title="See all creators"
                  minimumPlan="starter"
                  className="btn-primary ml-1"
                  onSuccess={() => router.push('/teams/billing')}
                />
              </div>
            </div>
          </div>
        )}
      </>
      {!!applicationId && (
        <Modal open onCancel={() => setApplicationId(undefined)}>
          <Application
            creator={creators.find((c) => c.applicationId === applicationId)}
            id={applicationId}
            onCancel={() => setApplicationId(undefined)}
            isProspect={list?.name === WORK_FOR_US_NAME}
          />
        </Modal>
      )}
    </>
  );
};
